// ClubConnect shared date picker — drill-down Year -> Month -> Day selection.
// Auto-applies to every <input type="date"> on the page. Opt a field out with data-no-datepicker.
// Respects the input's native min/max attributes (yyyy-MM-dd) to constrain selectable dates.
(function () {
    'use strict';

    const MONTH_NAMES = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const WEEKDAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    const YEARS_PER_PAGE = 20;

    // Default selectable range for any field that doesn't set its own min/max — wide enough to
    // cover both past-dated fields (birthdates) and future-dated fields (bookings) across portals.
    const DEFAULT_YEARS_PAST = 120;
    const DEFAULT_YEARS_FUTURE = 100;

    function pad2(value) {
        return value < 10 ? '0' + value : String(value);
    }

    function toIso(year, month, day) {
        return year + '-' + pad2(month + 1) + '-' + pad2(day);
    }

    function parseIso(value) {
        if (!value) {
            return null;
        }
        const parts = value.split('-');
        if (parts.length !== 3) {
            return null;
        }
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        if (isNaN(year) || isNaN(month) || isNaN(day)) {
            return null;
        }
        return { year: year, month: month, day: day };
    }

    function daysInMonth(year, month) {
        return new Date(year, month + 1, 0).getDate();
    }

    function formatDisplay(year, month, day) {
        return day + ' ' + MONTH_SHORT[month] + ' ' + year;
    }

    // The year grid always ends on the given year (rather than snapping to a fixed 20-year
    // bucket) so opening it lands on mostly-relevant years instead of a page dominated by
    // years that haven't happened yet.
    function yearPageStartEndingAt(year) {
        return year - YEARS_PER_PAGE + 1;
    }

    function compareDateParts(a, b) {
        if (a.year !== b.year) {
            return a.year - b.year;
        }
        if (a.month !== b.month) {
            return a.month - b.month;
        }
        return a.day - b.day;
    }

    function createDatepicker(input) {
        if (input.dataset.datepickerInit === 'true') {
            return;
        }
        input.dataset.datepickerInit = 'true';

        const today = new Date();
        const todayParts = { year: today.getFullYear(), month: today.getMonth(), day: today.getDate() };

        // A birthdate can never be in the future, regardless of portal — detected from the
        // field's name/id so no view needs to set max="..." itself. An explicit min/max
        // attribute always wins over both this and the generic default below.
        const fieldIdentifier = (input.name || input.id || '').toLowerCase();
        const looksLikeBirthdate = fieldIdentifier.indexOf('dateofbirth') !== -1;

        const minParts = parseIso(input.getAttribute('min')) || { year: todayParts.year - DEFAULT_YEARS_PAST, month: 0, day: 1 };
        const maxParts = parseIso(input.getAttribute('max')) ||
            (looksLikeBirthdate ? todayParts : { year: todayParts.year + DEFAULT_YEARS_FUTURE, month: 11, day: 31 });

        // Which view opens first when there's no existing value: birthdate fields jump straight
        // to the year grid (fastest path to an old birth year), everything else opens on today's
        // month like a normal calendar. Fields with an existing value always open on the day grid
        // showing that date, regardless of this setting. Override with
        // data-start-view="year|month|day" if a specific field ever needs something different.
        const startView = input.getAttribute('data-start-view') || (looksLikeBirthdate ? 'year' : 'day');

        let selected = parseIso(input.value);

        const wrapper = document.createElement('div');
        wrapper.className = 'datepicker';

        const display = document.createElement('input');
        display.type = 'text';
        display.className = input.className;
        display.classList.add('datepicker__display');
        display.readOnly = true;
        display.placeholder = input.placeholder || 'Select a date';

        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.className = 'datepicker__clear';
        clearBtn.setAttribute('aria-label', 'Clear date');
        clearBtn.textContent = '×';

        const panel = document.createElement('div');
        panel.className = 'datepicker__panel';
        panel.hidden = true;

        input.type = 'hidden';
        input.parentNode.insertBefore(wrapper, input);
        wrapper.appendChild(input);
        wrapper.appendChild(display);
        wrapper.appendChild(clearBtn);
        wrapper.appendChild(panel);

        function updateClearButton() {
            clearBtn.hidden = !selected;
        }

        if (selected) {
            display.value = formatDisplay(selected.year, selected.month, selected.day);
        }
        updateClearButton();

        clearBtn.addEventListener('click', function () {
            selected = null;
            input.value = '';
            display.value = '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
            updateClearButton();
            closePanel();
            // clearBtn hides itself as a result of this same click (see updateClearButton) —
            // restore focus explicitly so it doesn't jump to the next field on the page.
            display.focus();
        });

        let view = selected ? 'day' : startView;
        let viewYear = selected ? selected.year : todayParts.year;
        let viewMonth = selected ? selected.month : todayParts.month;
        let yearPageStart = yearPageStartEndingAt(viewYear);

        function withinRange(parts) {
            return compareDateParts(parts, minParts) >= 0 && compareDateParts(parts, maxParts) <= 0;
        }

        function renderHeader(label, onPrev, onNext, onLabelClick, hideNav) {
            const header = document.createElement('div');
            header.className = 'datepicker__header';

            if (!hideNav) {
                const prevBtn = document.createElement('button');
                prevBtn.type = 'button';
                prevBtn.className = 'datepicker__nav';
                prevBtn.setAttribute('aria-label', 'Previous');
                prevBtn.textContent = '‹';
                if (onPrev) {
                    prevBtn.addEventListener('click', onPrev);
                } else {
                    prevBtn.disabled = true;
                }
                header.appendChild(prevBtn);
            }

            const labelEl = document.createElement('button');
            labelEl.type = 'button';
            labelEl.className = 'datepicker__header-label';
            labelEl.textContent = label;
            if (onLabelClick) {
                labelEl.addEventListener('click', onLabelClick);
            } else {
                labelEl.disabled = true;
            }
            header.appendChild(labelEl);

            if (!hideNav) {
                const nextBtn = document.createElement('button');
                nextBtn.type = 'button';
                nextBtn.className = 'datepicker__nav';
                nextBtn.setAttribute('aria-label', 'Next');
                nextBtn.textContent = '›';
                if (onNext) {
                    nextBtn.addEventListener('click', onNext);
                } else {
                    nextBtn.disabled = true;
                }
                header.appendChild(nextBtn);
            }

            panel.appendChild(header);
        }

        function renderYearView() {
            const pageStart = yearPageStart;
            const pageEnd = pageStart + YEARS_PER_PAGE - 1;
            const canGoPrev = pageStart > minParts.year;
            const canGoNext = pageEnd < maxParts.year;

            renderHeader(
                pageStart + ' – ' + pageEnd,
                canGoPrev ? function () { yearPageStart -= YEARS_PER_PAGE; render(); } : null,
                canGoNext ? function () { yearPageStart += YEARS_PER_PAGE; render(); } : null,
                function () { yearPageStart = yearPageStartEndingAt(todayParts.year); render(); });

            const grid = document.createElement('div');
            grid.className = 'datepicker__grid datepicker__grid--year';

            for (let y = pageStart; y <= pageEnd; y++) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'datepicker__cell datepicker__cell--year';
                btn.textContent = String(y);

                if (y < minParts.year || y > maxParts.year) {
                    btn.disabled = true;
                    btn.classList.add('datepicker__cell--disabled');
                }
                if (selected && selected.year === y) {
                    btn.classList.add('datepicker__cell--selected');
                }

                btn.addEventListener('click', function () {
                    viewYear = y;
                    view = 'month';
                    render();
                });

                grid.appendChild(btn);
            }

            panel.appendChild(grid);
        }

        function renderMonthView() {
            const canGoPrev = viewYear > minParts.year;
            const canGoNext = viewYear < maxParts.year;

            renderHeader(
                String(viewYear),
                canGoPrev ? function () { viewYear -= 1; render(); } : null,
                canGoNext ? function () { viewYear += 1; render(); } : null,
                function () { yearPageStart = yearPageStartEndingAt(viewYear); view = 'year'; render(); });

            const grid = document.createElement('div');
            grid.className = 'datepicker__grid datepicker__grid--month';

            for (let m = 0; m < 12; m++) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'datepicker__cell datepicker__cell--month';
                btn.textContent = MONTH_SHORT[m];

                const monthOutOfRange =
                    (viewYear === minParts.year && m < minParts.month) ||
                    (viewYear === maxParts.year && m > maxParts.month);

                if (monthOutOfRange) {
                    btn.disabled = true;
                    btn.classList.add('datepicker__cell--disabled');
                }
                if (selected && selected.year === viewYear && selected.month === m) {
                    btn.classList.add('datepicker__cell--selected');
                }

                btn.addEventListener('click', function () {
                    viewMonth = m;
                    view = 'day';
                    render();
                });

                grid.appendChild(btn);
            }

            panel.appendChild(grid);
        }

        function renderDayView() {
            renderHeader(
                MONTH_NAMES[viewMonth] + ' ' + viewYear,
                null,
                null,
                function () { view = 'month'; render(); },
                true);

            const weekdays = document.createElement('div');
            weekdays.className = 'datepicker__grid datepicker__grid--weekdays';
            WEEKDAY_NAMES.forEach(function (name) {
                const span = document.createElement('span');
                span.className = 'datepicker__weekday';
                span.textContent = name;
                weekdays.appendChild(span);
            });
            panel.appendChild(weekdays);

            const grid = document.createElement('div');
            grid.className = 'datepicker__grid datepicker__grid--day';

            const firstWeekday = new Date(viewYear, viewMonth, 1).getDay();
            for (let i = 0; i < firstWeekday; i++) {
                const blank = document.createElement('span');
                blank.className = 'datepicker__cell datepicker__cell--blank';
                grid.appendChild(blank);
            }

            const totalDays = daysInMonth(viewYear, viewMonth);
            for (let d = 1; d <= totalDays; d++) {
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'datepicker__cell datepicker__cell--day';
                btn.textContent = String(d);

                const parts = { year: viewYear, month: viewMonth, day: d };
                if (!withinRange(parts)) {
                    btn.disabled = true;
                    btn.classList.add('datepicker__cell--disabled');
                }
                if (todayParts.year === viewYear && todayParts.month === viewMonth && todayParts.day === d) {
                    btn.classList.add('datepicker__cell--today');
                }
                if (selected && selected.year === viewYear && selected.month === viewMonth && selected.day === d) {
                    btn.classList.add('datepicker__cell--selected');
                }

                btn.addEventListener('click', function () {
                    selected = { year: viewYear, month: viewMonth, day: d };
                    input.value = toIso(viewYear, viewMonth, d);
                    display.value = formatDisplay(viewYear, viewMonth, d);
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                    updateClearButton();
                    closePanel();
                    // Some browsers (e.g. Firefox) move focus to the next focusable element
                    // in the document when the focused button's ancestor becomes hidden —
                    // without this, focus jumps to whatever field follows this one on the page.
                    display.focus();
                });

                grid.appendChild(btn);
            }

            panel.appendChild(grid);
        }

        function render() {
            panel.innerHTML = '';
            if (view === 'year') {
                renderYearView();
            } else if (view === 'month') {
                renderMonthView();
            } else {
                renderDayView();
            }
        }

        function handleOutsideClick(event) {
            if (!wrapper.contains(event.target)) {
                closePanel();
            }
        }

        function handleKeydown(event) {
            if (event.key === 'Escape') {
                closePanel();
                display.focus();
            }
        }

        // Keeps the panel fully on-screen horizontally. It's anchored left:0 by default (flush
        // with the input), which overflows off the right edge on mobile whenever the input sits
        // in a narrower column (e.g. a two-up grid) than the panel itself.
        function positionPanel() {
            panel.style.left = '0px';
            const rect = panel.getBoundingClientRect();
            const margin = 8;
            let offset = 0;

            if (rect.right > window.innerWidth - margin) {
                offset = (window.innerWidth - margin) - rect.right;
            }
            if (rect.left + offset < margin) {
                offset = margin - rect.left;
            }

            panel.style.left = offset + 'px';
        }

        function openPanel() {
            view = selected ? 'day' : startView;
            viewYear = selected ? selected.year : todayParts.year;
            viewMonth = selected ? selected.month : todayParts.month;
            yearPageStart = yearPageStartEndingAt(viewYear);
            render();
            panel.hidden = false;
            positionPanel();
            document.addEventListener('click', handleOutsideClick, true);
            document.addEventListener('keydown', handleKeydown, true);
        }

        function closePanel() {
            panel.hidden = true;
            document.removeEventListener('click', handleOutsideClick, true);
            document.removeEventListener('keydown', handleKeydown, true);
        }

        display.addEventListener('click', function () {
            if (panel.hidden) {
                openPanel();
            } else {
                closePanel();
            }
        });
    }

    function init() {
        const inputs = document.querySelectorAll("input[type='date']:not([data-no-datepicker])");
        inputs.forEach(function (input) { createDatepicker(input); });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
